import { cn } from '@/lib/utils'
import type { Rejection } from '@/lib/messaging-data'
import type { Freshness } from '@/lib/system-intelligence'
import { FreshnessNote, PanelEmpty, PanelError, PanelLoading } from './PanelState'

/** Recipient first and the timestamp last, as both mocks have it. */
const columns = ['Recipient', 'Outcome', 'Code', 'Reason', 'Timestamp (UTC)']

/**
 * Colour by outcome, not by code.
 *
 * The distinction that matters is whether something refused us or a recipient
 * chose to leave: a Twilio 21610 is a STOP reply and arrives as `unknown`, so it
 * stays neutral, while a carrier block or filter is red because it is ours to
 * act on.
 */
const outcomeClass: Record<string, string> = {
  blocked: 'border-critical/60 text-critical',
  carrier_filtered: 'border-critical/60 text-critical',
  bounced: 'border-degraded/60 text-degraded',
  dropped: 'border-degraded/60 text-degraded',
  unknown: 'border-line-bright text-fg-muted',
}

interface RejectionsTableProps {
  rejections: Rejection[]
  /** Panel heading — "Recent bounces" on email, "Recent delivery failures" on SMS. */
  title?: string
  /** Shown when there is nothing to list; worded for the channel. */
  emptyLabel?: string
  freshness?: Freshness
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
  /** Show more rows from the same feed. Absent when there are no more. */
  onShowMore?: () => void
}

/**
 * The last few messages the far end refused.
 *
 * Recipients arrive masked from the provider ("p***@outlook.con") and are shown
 * as they arrive — unmasking them is not something this screen can do, and
 * should not be. Reason codes are SendGrid's own and are left raw so a support
 * ticket, the vendor console and this table all say the same thing. A code that
 * means we were *blocked* is outlined in red: those are what accumulate into the
 * account blockage the banner reports, where a bad address is just a bad address.
 */
export function RejectionsTable({
  rejections,
  title = 'Recent Rejections',
  emptyLabel = 'Nothing has been rejected — no failures to show.',
  freshness,
  isLoading,
  error,
  onRetry,
  onShowMore,
}: RejectionsTableProps) {
  return (
    <section className="border-line bg-surface rounded-lg border border-dashed">
      <div className="border-line flex items-center justify-between gap-3 border-b px-5 py-4">
        <h2 className="text-fg-muted font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
          {title}
        </h2>
        {onShowMore && (
          <button
            type="button"
            onClick={onShowMore}
            className="text-info text-[13px] transition-opacity hover:opacity-80"
          >
            Show more
          </button>
        )}
      </div>

      {isLoading ? (
        <PanelLoading />
      ) : error ? (
        <PanelError error={error} onRetry={onRetry} />
      ) : rejections.length === 0 ? (
        <PanelEmpty label={emptyLabel} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr className="border-line border-b">
                  {columns.map((col) => (
                    <th
                      key={col}
                      className={cn(
                        'text-fg-subtle px-5 py-3 font-mono text-[11px] font-semibold tracking-[0.08em] uppercase',
                        col.startsWith('Timestamp') ? 'text-right' : 'text-left',
                      )}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rejections.map((row) => (
                  <tr
                    key={row.id}
                    className="border-line hover:bg-surface-2 border-b transition-colors"
                  >
                    <td className="text-fg px-5 py-4 font-mono text-[13px] whitespace-nowrap">
                      {row.recipient}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        title={row.outcomeHint}
                        className={cn(
                          'inline-block rounded border px-2.5 py-1 font-mono text-[11px] font-semibold tracking-[0.06em] uppercase',
                          outcomeClass[row.outcome] ?? outcomeClass.unknown,
                        )}
                      >
                        {row.outcome.replace('_', ' ')}
                      </span>
                    </td>
                    {/* The code carries the outcome's colour, as the SendGrid
                        mock has it — so a block reads red at a glance without a
                        second badge, and a 21610 STOP reply stays neutral. */}
                    <td className="px-5 py-4">
                      <span
                        className={cn(
                          'font-mono text-[13px]',
                          outcomeClass[row.outcome] ?? outcomeClass.unknown,
                          'border-0',
                        )}
                      >
                        {row.reasonCode}
                      </span>
                    </td>
                    <td className="text-fg px-5 py-4">{row.description}</td>
                    <td className="text-fg-muted px-5 py-4 text-right font-mono text-[13px] whitespace-nowrap">
                      {row.timestamp}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <FreshnessNote freshness={freshness} className="px-5 pb-4" />
        </>
      )}
    </section>
  )
}
